import React, { useState } from "react";
import {
  Button,
  Icons,
  Label,
  Pagination,
  RangeDatePicker,
  Select,
  Option,
  RangePickerValueType,
} from "@sede-enterprise/shell-ds-react-framework";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

import TransactionsTable from "../../components/transactionstable/TransactionsTable";
import { FilterNav } from "../../components/filternav/FilterNav";
import { Col, Row } from "../../components/flexbox/Flex";
import { ContainerDiv } from "../../components/styledComponent/StyledComponent";

import { ApiUrls } from "../../helpers/ApiUrls";
import { postData } from "../../services/ApiService";

import ResponseErrorModal from "../../components/responseErrorModal/ResponseErrorModal";
import { getDateXDaysAgo, usePageTitle } from "../../services/UtilService";
import Loader from "../../components/loader/Loader";
import { APIErrorResponseInterface } from "../../helpers/InterfacesConstants";
import { useTranslation } from "react-i18next";
import "./styles.css";
import { useToast } from "rc-toastr";

interface transactions {
  data: [];
  count: number;
}

interface transactionsRequestBody {
  searchTerm: string;
  dateFrom: string;
  dateTo: string;
  showResultBy: string;
  countryIds: string[];
  customerIds: string[];
  airportIds: string[];
  errorTypes?: string[];
  limit: number;
  offSet: number;
}

const DEFAULT_SHOW_RESULTS_BY = "ReceivedDate";
const DEFAULT_PAGE_SIZE = 10;

const getInitialDateRange = (
  filters?: Partial<transactionsRequestBody> | null
): RangePickerValueType => {
  if (filters?.dateFrom && filters?.dateTo) {
    return [dayjs(filters.dateFrom), dayjs(filters.dateTo)];
  }

  return null;
};

const getInitialFilters = (
  filters?: Partial<transactionsRequestBody> | null
): transactionsRequestBody => ({
  searchTerm: "",
  dateFrom: filters?.dateFrom || "",
  dateTo: filters?.dateTo || "",
  showResultBy: filters?.showResultBy || DEFAULT_SHOW_RESULTS_BY,
  countryIds: filters?.countryIds || [],
  customerIds: [],
  airportIds: filters?.airportIds || [],
  errorTypes: filters?.errorTypes || [],
  limit: DEFAULT_PAGE_SIZE,
  offSet: 0,
});

const Transactions = () => {
  const location = useLocation();
  const navigationFilters =
    location.state as Partial<transactionsRequestBody> | null;

  const [transactions, setTransactions] = useState<transactions>({
    count: 0,
    data: [],
  });

  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [dateRange, setDateRange] = useState<RangePickerValueType>(() =>
    getInitialDateRange(navigationFilters)
  );
  const [numOfDays, setNumOfDays] = useState<number | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const [apiErrorResponse, setApiErrorResponse] =
    useState<APIErrorResponseInterface | null>();
  const [filters, setFilters] = useState<transactionsRequestBody>(() =>
    getInitialFilters(navigationFilters)
  );

  const { t } = useTranslation();
  usePageTitle(t("shellAviationAIMS") + " | MI Transactions");

  const onPageChange = (current: number, pageSize: number) => {
    setPageSize(pageSize);
    setPageNumber(current);
    const requestBody: transactionsRequestBody = {
      ...filters,
      limit: pageSize,
      offSet: (current - 1) * pageSize,
    };
    setFilters(requestBody);
    getTransactions(requestBody);
  };

  const onFilterChange = (navFilters: transactionsRequestBody) => {
    const requestBody: transactionsRequestBody = {
      ...filters,
      ...navFilters,
      offSet: 0,
    };
    setPageNumber(1);
    setFilters(requestBody);
    getTransactions(requestBody);
  };

  const onShowResultsByChange = (value: string) => {
    const requestBody: transactionsRequestBody = {
      ...filters,
      showResultBy: value || DEFAULT_SHOW_RESULTS_BY,
      offSet: 0,
    };
    setPageNumber(1);
    setFilters(requestBody);
    getTransactions(requestBody);
  };

  const getTransactions = (requestBody: transactionsRequestBody) => {
    setIsLoading(true);
    postData(ApiUrls.miTransactions, requestBody)
      .then((response) => {
        setTransactions(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        setApiErrorResponse({
          message: error.response.data.message,
          traceId: error.response.data.traceId,
        });
        setIsLoading(false);
      });
  };

  const onDateRangeChange = (dateValue: RangePickerValueType) => {
    setDateRange(dateValue);

    if (dateValue) {
      const dateFrom: any = dateValue[0];
      const dateTo: any = dateValue[1];

      const monthFrom: string =
        dateFrom.$M + 1 < "10" ? "0" + (dateFrom.$M + 1) : dateFrom.$M + 1;
      const monthTo: string =
        dateTo.$M + 1 < "10" ? "0" + (dateTo.$M + 1) : dateTo.$M + 1;
      const dayFrom: string =
        dateFrom.$D < 10 ? "0" + dateFrom.$D : dateFrom.$D;
      const dayTo: string = dateTo.$D < 10 ? "0" + dateTo.$D : dateTo.$D;
      const requestBody: transactionsRequestBody = {
        ...filters,
        dateFrom: dateFrom.$y + "-" + monthFrom + "-" + dayFrom,
        dateTo: dateTo.$y + "-" + monthTo + "-" + dayTo,
        offSet: 0,
      };
      setPageNumber(1);
      setFilters(requestBody);
      getTransactions(requestBody);
    } else {
      const requestBody: transactionsRequestBody = {
        ...filters,
        dateFrom: "",
        dateTo: "",
        offSet: 0,
      };
      setPageNumber(1);
      setFilters(requestBody);
      getTransactions(requestBody);
    }
  };

  const onDateChange = (value: number) => {
    setDateRange(null);
    setNumOfDays(value);
    if (value && value != 5) {
      const toDate = new Date();
      const fromDate = getDateXDaysAgo(value);

      const monthFrom =
        fromDate.getMonth() + 1 < 10
          ? "0" + (fromDate.getMonth() + 1)
          : fromDate.getMonth() + 1;
      const monthTo =
        toDate.getMonth() + 1 < 10
          ? "0" + (toDate.getMonth() + 1)
          : toDate.getMonth() + 1;

      const dayFrom =
        fromDate.getDate() < 10 ? "0" + fromDate.getDate() : fromDate.getDate();
      const dayTo =
        toDate.getDate() < 10 ? "0" + toDate.getDate() : toDate.getDate();

      const requestBody: transactionsRequestBody = {
        ...filters,
        dateFrom: fromDate.getFullYear() + "-" + monthFrom + "-" + dayFrom,
        dateTo: toDate.getFullYear() + "-" + monthTo + "-" + dayTo,
        offSet: 0,
      };
      setPageNumber(1);
      setFilters(requestBody);
      getTransactions(requestBody);
    }
    if (value === undefined) {
      const requestBody: transactionsRequestBody = {
        ...filters,
        dateFrom: "",
        dateTo: "",
        offSet: 0,
      };
      setPageNumber(1);
      setFilters(requestBody);
      getTransactions(requestBody);
    }
  };

  const refreshPage = () => {
    setDateRange(null);
    setNumOfDays(null);
    const requestBody: transactionsRequestBody = {
      airportIds: [],
      countryIds: [],
      customerIds: [],
      dateFrom: "",
      dateTo: "",
      showResultBy: DEFAULT_SHOW_RESULTS_BY,
      errorTypes: [],
      limit: DEFAULT_PAGE_SIZE,
      offSet: 0,
      searchTerm: "",
    };
    setPageNumber(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setFilters(requestBody);
    getTransactions(requestBody);
  };

  //cr4 mi mass submission
  const { toast } = useToast();
  const [selectedMITransactions, setSelectedMITransactions] = useState<{
    ticketIds: string[];
    count: number;
  }>({ ticketIds: [], count: 0 });

  const handleMISelectionChange = (selectedIds: string[], count: number) => {
    setSelectedMITransactions({ ticketIds: selectedIds, count });
  };

  const handleMISubmitTransactions = async () => {
    try {
      const url = ApiUrls.MiBulkTransaction;
      const payload = { ticketIds: selectedMITransactions.ticketIds };
      const response = await postData(url, payload);
      toast.success(
        `${selectedMITransactions.count} transaction(s) submitted successfully.`
      );

      // Reset selection
      setSelectedMITransactions({ ticketIds: [], count: 0 });

      // Refresh table
      onFilterChange(filters);
    } catch (error: any) {
      setApiErrorResponse({
        message: error.response.data.message,
        traceId: error.response.data.traceId,
      });
    }
  };

  return (
    <>
      <ContainerDiv>
        <Row>
          <Col size="42">
            <Row>
              <Col size="55">
                <h2 style={{ margin: "0px", whiteSpace: "nowrap" }}>
                  MI Transactions
                </h2>
              </Col>
              <Col size="45">
                TOTAL <strong>{transactions.count}</strong>
                <span
                  style={{
                    display: "inline-block",
                    width: "13px",
                    height: "13px",
                    borderRadius: "10px",
                    marginLeft: "5px",
                    backgroundColor: "red",
                  }}
                ></span>
              </Col>
            </Row>
          </Col>
          <Col size="58">
            <div className="transactions-toolbar">
              <div className="transactions-toolbar-field">
                <div className="transactions-toolbar-label">
                  <Label bold>Days</Label>
                </div>
                <Select
                  onChange={(value) => {
                    onDateChange(value);
                  }}
                  value={numOfDays}
                >
                  <Option key="1" value="1">
                    Last 24 hours
                  </Option>
                  <Option key="2" value="3">
                    Last 3 days
                  </Option>
                  <Option key="3" value="7">
                    Last 7 days
                  </Option>
                  <Option key="4" value="30">
                    Last 30 days
                  </Option>
                  <Option key="5" value="90">
                    Last 90 days
                  </Option>
                  <Option key="6" value="5">
                    Custome Range
                  </Option>
                </Select>
              </div>
              <div className="transactions-toolbar-field transactions-toolbar-show-results">
                <div className="transactions-toolbar-label">
                  <Label bold>Show Results By</Label>
                </div>
                <Select
                  onChange={(value) => {
                    onShowResultsByChange(value);
                  }}
                  value={filters.showResultBy}
                >
                  <Option value="ReceivedDate">Received Date</Option>
                  <Option value="TransactionDate">Transaction Date</Option>
                </Select>
              </div>
              <div className="transactions-toolbar-field transactions-toolbar-date-range">
                <div className="transactions-toolbar-label">
                  <Label bold>Date Range</Label>
                </div>
                <RangeDatePicker
                  disabled={numOfDays != null && numOfDays != 5}
                  size="medium"
                  placeholder={["Start date", "Finish date"]}
                  value={dateRange}
                  onChange={(dateValue) => {
                    onDateRangeChange(dateValue);
                  }}
                />
              </div>
              <div className="transactions-toolbar-action">
                <Button
                  variant="outlined"
                  onClick={() => {
                    refreshPage();
                  }}
                >
                  <Icons.ArrowCw />
                  Refresh
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </ContainerDiv>

      <ContainerDiv>
        <FilterNav
          changeFilter={onFilterChange}
          isMITransactions={true}
          filters={filters}
          miSelectedCount={selectedMITransactions.count}
          onMISubmit={handleMISubmitTransactions}
        />
      </ContainerDiv>
      <ContainerDiv>
        {!isLoading && (
          <>
            <TransactionsTable
              data={transactions && transactions.data}
              refreshPage={refreshPage}
              onMISelectionChange={handleMISelectionChange}
            />
            <Pagination
              total={transactions.count}
              current={pageNumber}
              onChange={(pageNo, size) => {
                onPageChange(pageNo, size);
              }}
              pageSize={pageSize}
              showSizeChanger
            />
          </>
        )}

        {isLoading && <Loader />}
      </ContainerDiv>
      <ResponseErrorModal
        errorDetails={apiErrorResponse}
        isOpen={apiErrorResponse ? true : false}
        onModalClose={() => {
          setApiErrorResponse(null);
        }}
      ></ResponseErrorModal>
    </>
  );
};
export default Transactions;